import React from "react";
import { Link } from "react-router-dom";
import {
  WISHLIST_ITEMS,
  WISHLIST_CATEGORY_LABEL,
  type WishlistItem,
} from "@shared/wishlist";
import { WireDetailsPanel } from "./WireDetailsPanel";

/**
 * VITE_GIVE_LIVE flips the "Pay by card" CTA on every Wish List card. Stays
 * off until give.dotperson.org has WordPress/WooPayments/Stripe wired (Phase 0).
 * Set in apps/site/.env.production: VITE_GIVE_LIVE=true.
 */
const GIVE_LIVE: boolean =
  ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_GIVE_LIVE ?? "") === "true";

function formatCost(item: WishlistItem): string {
  const usd = item.cost_usd.toLocaleString("en-US");
  if (item.cadence === "monthly" && item.duration_months) {
    const monthly = Math.round(item.cost_usd / item.duration_months);
    return `US$${usd} · US$${monthly.toLocaleString("en-US")}/mo × ${item.duration_months}`;
  }
  if (item.cadence === "annual") return `US$${usd}/year`;
  return `US$${usd}`;
}

function ProgressBar({ raised, total }: { raised: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((raised / total) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${pct}% funded`}
      className="border border-hairline bg-paper-2 h-1.5 w-full"
    >
      <div className="bg-claude h-full" style={{ width: `${pct}%` }} />
    </div>
  );
}

function WishlistCard({ item }: { item: WishlistItem }) {
  const partialMonthlyAmount =
    item.cadence === "monthly" && item.duration_months
      ? Math.round(item.cost_usd / item.duration_months)
      : null;

  return (
    <article
      id={`wish-${item.id}`}
      className="border border-hairline bg-paper p-7 md:p-8 flex flex-col scroll-mt-24"
    >
      <header className="mb-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-claude font-bold">
            {WISHLIST_CATEGORY_LABEL[item.category]}
          </span>
          <span className="font-mono text-[11px] text-ink">
            {formatCost(item)}
          </span>
        </div>
        <h3 className="text-xl md:text-2xl font-serif text-ink mb-2 leading-tight">
          {item.name}
        </h3>
        <p className="text-sm font-serif italic text-ink-2 leading-snug">
          {item.short}
        </p>
      </header>

      <div className="text-sm text-ink-2 leading-relaxed space-y-3 mb-6">
        <p>
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mr-2">
            Why this
          </span>
          {item.why_this}
        </p>
        <p>
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mr-2">
            Unlocks
          </span>
          {item.what_it_unlocks}
        </p>
      </div>

      <div className="mt-auto space-y-4">
        <div>
          <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1.5">
            <span>
              US${item.raised_usd.toLocaleString("en-US")} raised
            </span>
            <span>of US${item.cost_usd.toLocaleString("en-US")}</span>
          </div>
          <ProgressBar raised={item.raised_usd} total={item.cost_usd} />
        </div>

        <details className="group border-t border-hairline pt-4">
          <summary className="list-none cursor-pointer flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-widest font-semibold bg-ink text-paper px-5 py-2.5 hover:bg-ink-2 transition-colors">
              Direct a gift
            </span>
            {GIVE_LIVE && (
              <Link
                to={`/give/wish/${item.id}`}
                onClick={(e) => e.stopPropagation()}
                className="text-xs uppercase tracking-widest font-semibold bg-claude text-paper px-5 py-2.5 hover:bg-claude/90 transition-colors"
              >
                Pay by card →
              </Link>
            )}
            {item.purchase_url && item.accepts_in_kind && (
              <a
                href={item.purchase_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs uppercase tracking-widest font-semibold border border-ink text-ink px-5 py-2.5 hover:bg-paper-2 transition-colors"
              >
                Buy &amp; ship to steward
              </a>
            )}
            {!item.accepts_in_kind && (
              <a
                href={`#contact`}
                onClick={(e) => e.stopPropagation()}
                className="text-xs uppercase tracking-widest font-semibold border border-ink text-ink px-5 py-2.5 hover:bg-paper-2 transition-colors"
              >
                Coordinate this gift
              </a>
            )}
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink-3 group-open:hidden">
              Show wire details ↓
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink-3 hidden group-open:inline">
              Hide wire details ↑
            </span>
          </summary>
          <div className="mt-4">
            <WireDetailsPanel
              itemId={item.id}
              notifyHref={`?wishItem=${item.id}#contact`}
            />
          </div>
        </details>

        {item.accepts_partial && partialMonthlyAmount && (
          <p className="text-[11px] font-mono text-ink-3 leading-relaxed">
            Accepts partial gifts — fund a single month at US$
            {partialMonthlyAmount.toLocaleString("en-US")} by referencing the
            same memo code.
          </p>
        )}
        {item.vendor && (
          <p className="text-[11px] font-mono text-ink-3">
            Vendor · {item.vendor}
          </p>
        )}
      </div>
    </article>
  );
}

export function WishlistGrid() {
  return (
    <ul
      role="list"
      className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 not-prose"
    >
      {WISHLIST_ITEMS.map((item) => (
        <li key={item.id}>
          <WishlistCard item={item} />
        </li>
      ))}
    </ul>
  );
}
